import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, ListChecks, Loader2, RefreshCw, Users } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { alternaLista, descriviPubblico, problemaPubblico } from '../../../lib/email/pubblico';
import type {
  EmailCampaignPreparata,
  EmailCampaignPubblico,
  EmailLista,
} from '../../../types/email.types';

interface SceltaDestinatariProps {
  pubblico: EmailCampaignPubblico;
  listeScelte: string[];
  liste: EmailLista[];
  /** Quanti contatti possono ricevere in tutto: il numero di "A tutti". */
  totaleAttivi: number;
  destinatari: EmailCampaignPreparata | null;
  calcolando: boolean;
  modificabile: boolean;
  /** Quante email si possono ancora spedire oggi. */
  quota: number;
  onCambia: (pubblico: EmailCampaignPubblico, liste: string[]) => void;
  onRicalcola: () => void;
}

/**
 * "A chi la mandi?": a tutti quelli che possono ricevere, oppure solo ad
 * alcune liste. Finche' non si sceglie, la campagna non parte: lo impone il
 * database, qui lo si dice prima.
 */
export function SceltaDestinatari({
  pubblico,
  listeScelte,
  liste,
  totaleAttivi,
  destinatari,
  calcolando,
  modificabile,
  quota,
  onCambia,
  onRicalcola,
}: SceltaDestinatariProps) {
  const scelta = { pubblico, liste: listeScelte };
  const problema = problemaPubblico(scelta);
  const nomi = Object.fromEntries(liste.map((l) => [l.id, l.name]));
  const giorni =
    destinatari && quota > 0 ? Math.ceil(destinatari.destinatari / Math.max(quota, 1)) : null;

  const opzione = (valore: 'tutti' | 'liste', titolo: string, sotto: string, Icona: typeof Users) => (
    <button
      type="button"
      onClick={() => onCambia(valore, listeScelte)}
      className={cn(
        'flex-1 text-left rounded-lg border p-3 transition-colors',
        pubblico === valore
          ? 'border-primary-500 bg-primary-50/60 ring-1 ring-primary-500'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      )}
    >
      <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <Icona className="w-4 h-4 text-primary-500" />
        {titolo}
      </span>
      <span className="block text-xs text-neutral-500 mt-1 leading-relaxed">{sotto}</span>
    </button>
  );

  if (!modificabile) {
    return (
      <Card className="border-gray-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Users className="w-5 h-5 text-neutral-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">{descriviPubblico(scelta, nomi)}</p>
            <p className="text-xs text-neutral-500 mt-0.5">
              {destinatari?.destinatari ?? 0} destinatari al momento dell'invio.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-gray-200">
      <CardContent className="p-4 space-y-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">A chi la mandi?</p>
          <p className="text-xs text-neutral-500 mt-0.5">
            Ricevono comunque solo i contatti che hanno dato il consenso e non sono bloccati.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          {opzione(
            'tutti',
            'A tutti',
            `Tutti i contatti che possono ricevere: oggi sono ${totaleAttivi}.`,
            Users
          )}
          {opzione(
            'liste',
            'Solo ad alcune liste',
            'Scegli una o piu\' liste: riceve chi e\' in almeno una di queste.',
            ListChecks
          )}
        </div>

        {pubblico === 'liste' &&
          (liste.length === 0 ? (
            <p className="text-xs text-neutral-500 rounded-lg border border-dashed border-gray-200 px-3 py-2.5">
              Non ci sono ancora liste. Si creano nella scheda{' '}
              <Link to="/mailing/liste" className="text-primary-700 underline underline-offset-2">
                Liste
              </Link>
              : crei la lista, premi "Aggiungi contatti" e spunti chi metterci.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {liste.map((l) => {
                const attiva = listeScelte.includes(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => onCambia('liste', alternaLista(listeScelte, l.id))}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                      attiva
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-neutral-700 border-gray-200 hover:border-primary-300'
                    )}
                    title={`${l.possono_ricevere} su ${l.contatti} possono ricevere`}
                  >
                    {l.name}
                    <span
                      className={cn(
                        'rounded-full px-1.5 text-[10px] font-semibold',
                        attiva ? 'bg-white/20 text-white' : 'bg-neutral-100 text-neutral-500'
                      )}
                    >
                      {l.possono_ricevere}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}

        <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="min-w-0 text-xs">
            {problema ? (
              <span className="inline-flex items-center gap-1.5 text-amber-800 font-medium">
                <AlertTriangle className="w-3.5 h-3.5" />
                {problema}
              </span>
            ) : calcolando ? (
              <span className="inline-flex items-center gap-1.5 text-neutral-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Calcolo i destinatari…
              </span>
            ) : destinatari ? (
              <>
                <span className="text-sm font-semibold text-gray-900">
                  {destinatari.destinatari} destinatari
                </span>
                {destinatari.esclusi_per_consenso > 0 && (
                  <span className="text-neutral-500">
                    {' '}
                    · altri {destinatari.esclusi_per_consenso} restano fuori perche' non hanno un
                    consenso valido
                  </span>
                )}
                {giorni && giorni > 1 && (
                  <span className="flex items-center gap-1.5 text-amber-700 mt-1">
                    <Clock className="w-3.5 h-3.5" />
                    Con il tetto di {quota} email al giorno serviranno circa {giorni} giorni.
                  </span>
                )}
              </>
            ) : (
              <span className="text-neutral-500">Destinatari da calcolare</span>
            )}
          </div>
          {!problema && (
            <Button variant="ghost" size="sm" onClick={onRicalcola} disabled={calcolando} className="flex-shrink-0">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Ricalcola
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
